import axios, { AxiosInstance, AxiosRequestConfig, AxiosProgressEvent } from 'axios';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../components/auth/AuthProvider';

interface ApiClient {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => Promise<T>;
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => Promise<T>;
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => Promise<T>;
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => Promise<T>;
  postMultipart: <T = any>(
    url: string,
    formData: FormData,
    options?: { onUploadProgress?: (evt: AxiosProgressEvent) => void; timeoutMs?: number }
  ) => Promise<T>;
}

const AUTH_PROVIDER = (process.env.REACT_APP_AUTH_PROVIDER || 'AZURE_AD').toUpperCase();

export const useApiClient = (): ApiClient => {
  const { apiToken, acquireApiToken, logout } = useAuth() as any;
  const refreshingRef = useRef<Promise<string | null> | null>(null);
  const acquireRef = useRef(acquireApiToken);
  const logoutRef = useRef(logout);

  // Keep latest acquireApiToken in a ref so interceptors don't force rememoization
  useEffect(() => {
    acquireRef.current = acquireApiToken;
    logoutRef.current = logout;
  }, [acquireApiToken, logout]);

  // Create axios instance with base configuration
  const createAxiosInstance = useCallback((): AxiosInstance => {
    const instance = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1',
      // Increase base timeout; specific calls can override further
      timeout: 120000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include API token for every request
    instance.interceptors.request.use(
      async (config) => {
        // Prefer cached token; avoid re-acquiring on every request
        const tokenToUse = apiToken || null;
        if (tokenToUse) {
          (config.headers = config.headers || {}).Authorization = `Bearer ${tokenToUse}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for concise error logging and 401 handling
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as any;
        // Structured, compact error log (single entry per failure)
        try {
          const res = error.response;
          const req = error.config || {};
          const method = (req.method || 'GET').toString().toUpperCase();
          const url = req.baseURL ? `${req.baseURL}${req.url || ''}` : (req.url || '');
          const status = res?.status;
          const cid = res?.headers?.['x-correlation-id'] || res?.data?.correlation_id;
          const errBody = res?.data?.error || res?.data || {};
          const code = errBody?.code;
          const message = errBody?.message || error.message;
          // One-line summary + minimal structured payload
          // eslint-disable-next-line no-console
          console.error(
            `[API] ${method} ${url} -> ${status ?? 'ERR'}` + (cid ? ` (cid: ${cid})` : ''),
            {
              status,
              code,
              message,
              cid,
            }
          );
        } catch (_) {
          // swallow logging errors
        }

        // Network error path: no response object
        if (!error.response) {
          const userMessage = 'Unable to reach the server. Please check your network connection and try again.';
          (error as any).userMessage = userMessage;
          try {
            window.dispatchEvent(
              new CustomEvent('api-error', {
                detail: { status: undefined, userMessage, correlationId: undefined },
              })
            );
          } catch {}
          return Promise.reject(error);
        }

        // Build a normalized user-friendly message for the UI
        try {
          const res = error.response;
          const status = res?.status as number | undefined;
          const errBody = res?.data?.error || res?.data || {};
          const backendMessage: string | undefined = errBody?.message || res?.data?.detail || error.message;
          let userMessage = backendMessage || 'Request failed';
          if (status === 401) {
            userMessage = 'Your session has expired or is invalid. Please sign in again.';
          } else if (status === 403) {
            const msg = (backendMessage || '').toLowerCase();
            if (msg.includes("user:manage")) {
              userMessage = "You don't have permission to manage users. Please contact an administrator.";
            } else if (msg.includes('forbidden') || msg.includes('permission')) {
              userMessage = "You don't have permission to perform this action.";
            } else {
              userMessage = 'Access denied. Please check your role or permissions.';
            }
          } else if (status === 404) {
            userMessage = 'The requested resource was not found.';
          } else if (status === 409) {
            // leave specific 409 handling to feature UIs (e.g., categories) but keep a default
            userMessage = backendMessage || 'A conflict occurred with existing data.';
          } else if (status && status >= 500) {
            userMessage = 'Server error. Please try again or contact support.';
          }
          (error as any).userMessage = userMessage;

          // Global dispatch so an app-wide listener can optionally show a toast
          try {
            const cid = res?.headers?.['x-correlation-id'] || res?.data?.correlation_id;
            window.dispatchEvent(
              new CustomEvent('api-error', {
                detail: { status, userMessage, correlationId: cid },
              })
            );
          } catch (_) {
            // ignore
          }
        } catch (_) {
          // no-op
        }
        if (error.response?.status === 401) {
          // DB provider: token is expired or invalid; clear session and surface error
          if (AUTH_PROVIDER === 'DB') {
            try { await logoutRef.current?.(); } catch {}
            return Promise.reject(error);
          }
          // Azure AD: single retry after silent refresh
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            if (!refreshingRef.current) {
              refreshingRef.current = acquireRef.current().finally(() => {
                refreshingRef.current = null;
              });
            }
            const newToken = await refreshingRef.current;
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return instance(originalRequest);
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [apiToken]);

  // Keep axios instance in a ref so returned methods remain stable
  const axiosRef = useRef<AxiosInstance>(createAxiosInstance());
  useEffect(() => {
    axiosRef.current = createAxiosInstance();
  }, [createAxiosInstance]);

  // In-flight de-dup map for GET requests
  const inflight = useRef<Map<string, Promise<any>>>(new Map());

  const client = useMemo<ApiClient>(() => ({
    get: async <T = any>(url: string, config?: AxiosRequestConfig) => {
      const key = `GET:${url}:${JSON.stringify(config || {})}`;
      if (inflight.current.has(key)) {
        return inflight.current.get(key)! as Promise<T>;
      }
      const p = axiosRef.current.get<T>(url, config)
        .then(res => res.data as T)
        .finally(() => inflight.current.delete(key));
      inflight.current.set(key, p);
      return p;
    },
    post: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
      const res = await axiosRef.current.post<T>(url, data, config);
      return res.data as T;
    },
    put: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
      const res = await axiosRef.current.put<T>(url, data, config);
      return res.data as T;
    },
    delete: async <T = any>(url: string, config?: AxiosRequestConfig) => {
      const res = await axiosRef.current.delete<T>(url, config);
      return res.data as T;
    },
    patch: async <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
      const res = await axiosRef.current.patch<T>(url, data, config);
      return res.data as T;
    },
    postMultipart: async <T = any>(
      url: string,
      formData: FormData,
      options?: { onUploadProgress?: (evt: AxiosProgressEvent) => void; timeoutMs?: number }
    ) => {
      // Ensure multipart header overrides default JSON
      const res = await axiosRef.current.post<T>(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Allow up to 5 minutes by default for server-side processing
        timeout: options?.timeoutMs ?? 300000,
        onUploadProgress: options?.onUploadProgress,
      });
      return res.data as unknown as T;
    },
  }), []);

  return client;
};
